import React from "react";
import { useNavigate } from "react-router-dom";
import { Center, Environment, Grid, OrbitControls } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import SetSceneBackgroundColor from "../components/SetSceneBackgroundColor";
import Result from "../components/Result";
import { useGenerations } from "../contexts/GenerationsContext";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

function Generations() {
  const navigate = useNavigate();
  const {
    generations,
    loading,
    handlePreviousPage,
    handleNextPage,
    currentPage,
    totalPages,
  } = useGenerations();

  const gridConfig = {
    gridSize: [10.5, 10.5],
    cellSize: 0.6,
    cellThickness: 1,
    cellColor: "#9d4b4b",
    sectionSize: 3.3,
    sectionThickness: 1.5,
    sectionColor: "#9d4b4b",
    fadeDistance: 25,
    fadeStrength: 1,
    followCamera: false,
    infiniteGrid: true,
  };
  const { gridSize, ...restGridConfig } = gridConfig;

  return (
    <div className="h-full overflow-auto pb-20">
      <h1 className="text-foreground pl-14 pt-10 text-3xl font-bold">
        Generation Library
      </h1>

      <div className="max-w-[850px]">
        {loading && (
          <div className="grid grid-cols-3 gap-10 p-14 pb-8 max-w-[850px]">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="aspect-square min-h-40 rounded-lg border-2 border-border" />
            ))}
          </div>
        )}

        {!loading && generations.length === 0 && (
          <div className="mx-14 mt-10 mb-8 neo-surface border-2 rounded-lg p-8 neo-shadow text-center">
            <h2 className="text-foreground text-xl font-semibold mb-2">
              No generations yet
            </h2>
            <p className="text-foreground/70 mb-6">
              Your generated 3D models will appear here, ready to revisit and
              re-export any time.
            </p>
            <Button onClick={() => navigate("/")}>Create your first 3D model</Button>
          </div>
        )}

        {!loading && generations.length > 0 && (
        <div className="grid grid-cols-3 gap-10 p-14 pb-8 max-w-[850px]">
          {generations.map((result, index) => (
            <div key={index} className="aspect-square min-h-40 relative">
              <Canvas
                shadows
                camera={{ position: [10, 12, 12], fov: 25 }}
                gl={{ alpha: false }}
                style={{ background: "var(--color-secondary-background)" }}
                className={"rounded-lg border-2 border-border neo-shadow"}
              >
                <SetSceneBackgroundColor />
                <group position={[0, -0.5, 0]}>
                  <Center top>
                    <Result url={result} showWireframe={false} />
                  </Center>
                  <Grid
                    position={[0, -0.01, 0]}
                    args={[gridSize[0], gridSize[1]]}
                    {...restGridConfig}
                  />
                </group>
                <OrbitControls makeDefault />
                <Environment
                  files="potsdamer_platz_1k.hdr"
                  background={false}
                />
              </Canvas>

              <Button
                size="sm"
                className="absolute bottom-2 right-2 text-xs h-6"
                onClick={() => navigate("/", { state: { result } })}
              >
                Export
              </Button>
            </div>
          ))}
        </div>
        )}

        {!loading && totalPages > 1 && (
        <div className="pagination-buttons text-foreground flex justify-center gap-4 mb-8">
          <Button variant="secondary" onClick={handlePreviousPage} disabled={currentPage === 1}>
            <span className="mr-1">&larr;</span> Previous
          </Button>
          <Button variant="secondary" onClick={handleNextPage} disabled={currentPage === totalPages}>
            Next <span className="ml-1">&rarr;</span>
          </Button>
        </div>
        )}

        {/* Create Generation Section */}
        <div className="px-14 flex flex-col gap-10">
          <div className="neo-surface border-2 rounded-lg p-8 neo-shadow">
            <h2 className="text-foreground text-xl font-semibold mb-4">Create</h2>
            <Button onClick={() => navigate("/")}>Create a New Generation</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Generations;
